import type { useAccessRequests } from '@confluence/access-requests/entry-points/useAccessRequests';
import type { PublicLinkPageProviderData } from '@confluence/public-links/entry-points/usePublicLinkPageDataProvider';
import type { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery';

import type { ContentType } from '../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator';
import type { QueriesState } from '../Controller';
import type {
	GeneralAccessLevel,
	Principal,
	PrincipalAndRole,
	Role,
} from '../../controlled/commonTypes';
import { getCachedInvites } from '../../controlled/sessionStorage';

import { getContentRestrictions } from './getContentRestrictions';
import { getCurrentUserPrincipal } from './getCurrentUserPrincipal';
import type {
	ShareAndRestrictDialogQuery_content_nodes_ancestors,
	ShareAndRestrictDialogQuery as ShareAndRestrictDialogQueryType,
} from './__types__/ShareAndRestrictDialogQuery';
import type {
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild as ContentData,
	useQueriesShareAndRestrictDialogQuery_Query as QueryData,
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild_nodes_Content_restrictions_ContentRestrictions as ContentRestrictions,
} from './__types__/useQueriesShareAndRestrictDialogQuery';
import type { GroupAndTeamCountsMapType } from './useGroupsCountQuery';
import type { UseTeamDataResult } from './useTeamData';

const addGroupAndTeamData = (
	restrictions: ContentRestrictions,
	groupAndTeamCountsMap: GroupAndTeamCountsMapType,
	teamData: UseTeamDataResult['teamData'],
): ContentRestrictions & {
	groupAndTeamCountsMap: GroupAndTeamCountsMapType;
	teamData: UseTeamDataResult['teamData'];
} => ({
	...restrictions,
	groupAndTeamCountsMap,
	teamData,
});

/**
 * Translates all main query data into an internal format. Returns `null` if
 * the query resolved with too many nulls to be able to display the dialog in
 * any form.
 */
export function dataToQueriesState(
	data: QueryData,
	publicLinkPageData: PublicLinkPageProviderData,
	spaceKey: string,
	contentType: ContentType,
	edition: ConfluenceEdition,
	disableSharingToEmails: boolean = false,
	groupAndTeamCountsMap: GroupAndTeamCountsMapType,
	teamData: UseTeamDataResult['teamData'],
	requestedUsersPendingApproval: ReturnType<typeof useAccessRequests>['requests'],
): QueriesState | null {
	const currentUser = getCurrentUserPrincipal(data.user);
	if (!currentUser) return null;

	const contentData = getContentData(data.content, currentUser, groupAndTeamCountsMap, teamData);
	if (!contentData) return null;

	const principalsPendingAccess = getPrincipalsPendingAccess(
		contentData.contentRestrictions.principals,
		requestedUsersPendingApproval,
	);

	return {
		...contentData,
		spaceKey,
		currentUser,
		publicLinkPageData,
		userCanEditContent: canEditContent(data, contentType),
		userCanEditRestrictions: canSetRestrictions(
			data,
			contentData.contentRestrictions.generalAccessLevel,
			contentType,
		),
		edition,
		disableSharingToEmails,
		principalsPendingAccess,
	};
}

export function canEditContent(
	data: ShareAndRestrictDialogQueryType | undefined,
	contentType: ContentType,
) {
	const contentNode = data?.content?.nodes?.[0];
	const operationsData = contentNode?.operations || [];

	return operationsData.some(
		(operation) => operation?.operation === 'update' && operation?.targetType === contentType,
	);
}

/**
 * Determines if the current user can set restrictions on the content.
 */
export function canSetRestrictions(
	data: ShareAndRestrictDialogQueryType | undefined,
	generalAccessLevel: GeneralAccessLevel,
	contentType: ContentType,
) {
	const contentNode = data?.content?.nodes?.[0];
	const operationsData = contentNode?.operations || [];
	const isSpaceAdmin = data?.space?.currentUser?.isAdmin || false;

	// Check if the current content type has a restrict_content operation
	const canRestrictContent = operationsData.some(
		(operation) =>
			operation?.operation === 'restrict_content' && operation?.targetType === contentType,
	);

	if (generalAccessLevel === 'open-can-edit') return canRestrictContent;

	const userHasEditRestrictions = operationsData.some(
		(operation) => operation?.operation === 'update' && operation?.targetType === contentType,
	);

	return (canRestrictContent && userHasEditRestrictions) || isSpaceAdmin;
}

/**  Extracts all the QueriesState data that can be obtained from the `content` node */
function getContentData(
	content: ContentData | null,
	currentUser: Principal,
	groupAndTeamCountsMap: GroupAndTeamCountsMapType,
	teamData: UseTeamDataResult['teamData'],
): Pick<
	QueriesState,
	| 'contentRestrictions'
	| 'hasInheritedRestrictions'
	| 'parentPageLink'
	| 'groupCount'
	| 'teamCount'
	| 'numberOfAncestors'
	| 'currentUserHasAccess'
> | null {
	if (!content) return null;

	const { nodes } = content;
	if (!nodes) return null;

	const [node0] = nodes;
	if (!node0) return null;

	const { restrictions, hasInheritedRestrictions, ancestors } = node0;
	if (!restrictions) return null;

	const restrictionsWithGroupAndTeamData = addGroupAndTeamData(
		restrictions,
		groupAndTeamCountsMap,
		teamData,
	);

	const contentRestrictionsAndCounts = getContentRestrictions(
		restrictionsWithGroupAndTeamData,
		currentUser,
	);

	if (!contentRestrictionsAndCounts) return null;

	const parentPageLink = hasInheritedRestrictions
		? getClosestAncestorWithViewRestrictions(ancestors)
		: null;

	return {
		contentRestrictions: {
			generalAccessLevel: contentRestrictionsAndCounts.generalAccessLevel,
			principals: contentRestrictionsAndCounts.principals,
		},
		numberOfAncestors: ancestors?.length || 0,
		groupCount: contentRestrictionsAndCounts.groupCount,
		teamCount: contentRestrictionsAndCounts.teamCount,
		currentUserHasAccess: contentRestrictionsAndCounts.currentUserHasAccess,
		hasInheritedRestrictions,
		parentPageLink,
	};
}

export function getClosestAncestorWithViewRestrictions(
	ancestors: (ShareAndRestrictDialogQuery_content_nodes_ancestors | null)[] | null,
): string | null | undefined {
	const closestBoundingPage: {
		id: string;
		link?: string | null;
	} = { id: '', link: null };
	// Last ancestor is closest bounding
	if (ancestors)
		for (let i = ancestors.length - 1; i >= 0; i--) {
			if (ancestors[i]?.hasViewRestrictions) {
				closestBoundingPage.id = ancestors[i]?.id || '';
				closestBoundingPage.link = ancestors[i]?.links?.webui;
				break;
			}
		}

	return closestBoundingPage.link;
}

export function getPrincipalsPendingAccess(
	existingPrincipals: PrincipalAndRole[],
	requestedUsersPendingApproval: ReturnType<typeof useAccessRequests>['requests'],
): PrincipalAndRole[] {
	return requestedUsersPendingApproval
		.filter((node): node is NonNullable<typeof node> => node !== null)
		.filter(
			(node) =>
				!existingPrincipals.some(
					(principal) => principal.principal.id === node.accessRequestedAaid,
				),
		)
		.map((node) => {
			const avatarUrl = node.user?.photos?.find((photo) => photo?.isPrimary === true)?.value ?? '';
			const email = node.user?.emails?.find((e) => e?.isPrimary === true)?.value ?? '';
			const cachedInvites = getCachedInvites();
			const knownErrors = {
				accessWarning: cachedInvites.includes(node.accessRequestedAaid)
					? ('pending-userbase-propagation-warning' as const)
					: ('pending-site-access-warning' as const),
				parentLink: null,
			};
			const principal = {
				type: 'user' as const,
				id: node.accessRequestedAaid,
				name: node.user?.displayName ?? '',
				avatarUrl,
				email,
				knownErrors,
				sitePermissionType: '',
			};
			let role: Role;
			if (node.requestAccessType === 'EDIT') {
				role = 'can-edit';
			} else if (node.requestAccessType === 'VIEW') {
				role = 'can-view';
			} else {
				role = 'no-access';
			}
			return {
				principal,
				role,
			};
		});
}
